import React from 'react';
import { Search, X, Calendar, ChevronDown, RotateCcw } from 'lucide-react';
import { Panel, Select, MultiSelect, btn, field } from './AdminUI';
import { PaymentStatusFilter, UnpaidSinceFilter, OverdueDaysFilter } from './InvoiceFilters';

/**
 * The one filter bar the super admin, admin and accountant consoles share.
 *
 * Every control grows from the width its own content wants, up to a cap. A row
 * therefore fills edge to edge however many controls land on it, and none of
 * them balloons: an even grid gave a 160px picker a 380px cell, and letting the
 * search take all the slack left the billing filters stranded on a short second
 * row. The three billing filters still travel as one group, so what wraps is a
 * group rather than a straggler.
 *
 * Only the controls a console passes something for are drawn, so the admin's
 * single-user picker and the super admin's multi-selects both fit.
 */
const ShipmentFilters = ({
  // who — a single picker { value, onChange, options, allLabel }
  user,
  customers, selectedCustomers, onToggleCustomer, onClearCustomers,
  admins, selectedAdmins, onToggleAdmin, onClearAdmins,
  destinations, selectedDests, onToggleDest, onClearDests,
  // what
  search, onSearch, searchPlaceholder = 'Search…',
  dateLabel, onOpenDates,
  // billing
  paymentFilter, onPaymentFilter,
  unpaidSince, onUnpaidSince, unpaidOpen, onUnpaidOpen,
  overdueDays, onOverdueDays,
  // the rest
  facets = [], onReset, actions,
}) => {
  const hasBilling = onPaymentFilter || onUnpaidSince || onOverdueDays;

  return (
    <Panel className="px-4 sm:px-5 py-3.5 mb-5">
      <div className="flex flex-wrap items-center gap-2">
        {user && (
          <Select
            className="flex-[1_1_160px] max-w-[260px]"
            value={user.value}
            onChange={user.onChange}
            options={user.options}
            allLabel={user.allLabel || 'All users'}
          />
        )}

        {customers && (
          <MultiSelect
            className="flex-[1_1_160px] max-w-[260px]"
            options={customers}
            selected={selectedCustomers}
            onToggle={onToggleCustomer}
            onClear={onClearCustomers}
            allLabel="All clients"
            nounPlural="clients"
          />
        )}

        {admins && (
          <MultiSelect
            className="flex-[1_1_145px] max-w-[230px]"
            options={admins}
            selected={selectedAdmins}
            onToggle={onToggleAdmin}
            onClear={onClearAdmins}
            allLabel="All admins"
            nounPlural="admins"
          />
        )}

        {destinations && (
          <MultiSelect
            className="flex-[1_1_165px] max-w-[260px]"
            options={destinations}
            selected={selectedDests}
            onToggle={onToggleDest}
            onClear={onClearDests}
            allLabel="All destinations"
            nounPlural="destinations"
          />
        )}

        {onOpenDates && (
          <button type="button" className={`${btn.quiet} flex-[1_1_165px] max-w-[260px] justify-between px-3`} onClick={onOpenDates}>
            <span className="inline-flex items-center gap-2 min-w-0">
              <Calendar size={15} className="flex-shrink-0 text-slate-400" />
              <span className="truncate">{dateLabel}</span>
            </span>
            <ChevronDown size={15} className="flex-shrink-0 text-slate-400" />
          </button>
        )}

        {/* It takes more of the slack than the pickers, but not all of it. */}
        {onSearch && (
          <div className="relative flex-[2_1_220px] max-w-[420px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={`${field} pl-9 pr-8`}
            />
            {search && (
              <button type="button" onClick={() => onSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {hasBilling && (
          <>
            <div className="flex items-center gap-2 flex-[1_1_480px]">
              {onPaymentFilter && (
                <PaymentStatusFilter className="flex-[1_1_0] min-w-[140px]" value={paymentFilter} onChange={onPaymentFilter} />
              )}
              {onUnpaidSince && (
                <UnpaidSinceFilter
                  className="flex-[1_1_0] min-w-[155px]"
                  value={unpaidSince}
                  onChange={onUnpaidSince}
                  open={unpaidOpen}
                  onOpenChange={onUnpaidOpen}
                />
              )}
              {onOverdueDays && (
                <OverdueDaysFilter className="flex-[1_1_0] min-w-[150px]" value={overdueDays} onChange={onOverdueDays} />
              )}
            </div>
          </>
        )}
      </div>

      {(actions || facets.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          {facets.length > 0 && (
            <>
              <span className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400">Filters</span>
              {facets.map((f) => (
                <button key={f.key} type="button" onClick={f.clear}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold
                             bg-blue-50 text-blue-700 hover:bg-blue-100 transition
                             dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25">
                  {f.label}
                  <X size={12} />
                </button>
              ))}
              <button type="button" onClick={onReset} className={`${btn.quiet} h-8 px-2.5 text-[12px]`}>
                <RotateCcw size={13} /> Reset
              </button>
            </>
          )}
          {actions && <div className="flex flex-wrap items-center gap-2.5 ml-auto">{actions}</div>}
        </div>
      )}
    </Panel>
  );
};

export default ShipmentFilters;
